import { uniqueId } from '../../utilities/dom.js';
import { changeElementType } from './ProgressiveDialog.helpers.js';

/**
 * @customElement progressive-dialog
 */
export class ProgressiveDialog extends HTMLElement {

  /**
   * @param {string} [tagName]
   * @param {CustomElementRegistry} [registry]
   * @returns {typeof ProgressiveDialog}
   */
  static define(tagName = 'progressive-dialog', registry = customElements) {
    registry.define(tagName, ProgressiveDialog);
    return this;
  }

  /**
   * @type {?HTMLDialogElement}
   */
  #invokee = null;

  /**
   * @type {HTMLAnchorElement[]}
   */
  #invokers = [];

  /**
   * @returns {void}
   */
  connectedCallback() {
    const element = /** @type {?HTMLElement} */ (this.querySelector('[data-dialog]'));

    if (! element) {
      throw new Error(`<${this.localName}> requires a child element with a "data-dialog" attribute.`);
    }

    this.#invokee = changeElementType(element, 'dialog');
    this.#invokee.id ||= uniqueId('progressive-dialog');

    this.#invokee.addEventListener('close', this);
    this.#invokee.setAttribute('aria-label', element.dataset.dialog);
    this.#invokee.removeAttribute('data-dialog');

    this.#invokers = Array.from(
      document.querySelectorAll(`a[data-dialog-action][data-dialog-target="${this.#invokee.id}"]`)
    );

    this.#enhanceInvokers(this.#invokee.open);
  }

  /**
   * @returns {void}
   */
  disconnectedCallback() {
    this.#invokee?.close();
    this.#invokee?.removeEventListener('close', this);

    this.#cleanupInvokers();

    this.#invokers = [];
    this.#invokee = null;
  }

  /**
   * @param {Event} event
   */
  handleEvent(event) {
    const {type} = event;

    if (type === 'click') {
      this.#handleClick(/** @type {MouseEvent} */ (event));
    } else if (type === 'keydown') {
      this.#handleKeydown(/** @type {KeyboardEvent} */ (event));
    } else if (type === 'close') {
      this.#handleClose(/** @type {Event} */ (event));
    }
  }

  /**
   * @param {'show-modal'|'show'|'close'|string} command
   * @param {{
    *  source?: HTMLAnchorElement,
    *  returnValue?: string,
   * }} [options]
   */
  #executeCommand(command, options = {}) {
    const { returnValue = null } = options;

    if (command === 'show-modal') {
      this.#invokee.setAttribute('aria-modal', 'true');
      this.#invokee.setAttribute('closedby', 'any');
      this.#invokee.returnValue = '';
      this.#invokee.showModal();
      this.#updateInvokers(true);
    } else if (command === 'show') {
      this.#invokee.removeAttribute('aria-modal');
      this.#invokee.removeAttribute('closedby');
      this.#invokee.returnValue = '';
      this.#invokee.show();
      this.#updateInvokers(true);
    } else if (command === 'close') {
      this.#invokee.close(returnValue);
    }
  }

  /**
   * @param {boolean} open
   */
  #enhanceInvokers(open) {
    this.#invokers.forEach((invoker) => {
      const {dialogAction} = invoker.dataset;

      invoker.setAttribute('role', 'button');

      if (dialogAction === 'show-modal' || dialogAction === 'show') {
        invoker.setAttribute('aria-haspopup', 'dialog');
        invoker.setAttribute('aria-expanded', String(open));
      }

      invoker.addEventListener('click', this);
      invoker.addEventListener('keydown', this);
    });
  }

  /**
   * @param {boolean} open
   */
  #updateInvokers(open) {
    this.#invokers.forEach((invoker) => {
      const {dialogAction} = invoker.dataset;

      if (dialogAction === 'show-modal' || dialogAction === 'show') {
        invoker.setAttribute('aria-expanded', String(open));
      }
    });
  }

  /**
   * @returns {void}
   */
  #cleanupInvokers() {
    this.#invokers.forEach((invoker) => {
      invoker.removeAttribute('role');
      invoker.removeAttribute('aria-haspopup');
      invoker.removeAttribute('aria-expanded');

      invoker.removeEventListener('click', this);
      invoker.removeEventListener('keydown', this);
    });
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick(event) {
    const source = /** @type {HTMLAnchorElement} */ (event.currentTarget);

    this.#executeCommand(source.dataset.dialogAction, {
      source: source,
      returnValue: source.dataset.dialogReturn,
    });

    event.preventDefault();
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown(event) {
    const {key} = event;

    if (key !== ' ') {
      return;
    }

    const source = /** @type {HTMLAnchorElement} */ (event.currentTarget);

    this.#executeCommand(source.dataset.dialogAction, {
      source: source,
      returnValue: source.dataset.dialogReturn,
    });

    event.preventDefault();
  }

  /**
   * @param {Event} _event
   */
  #handleClose(_event) {
    this.#updateInvokers(false);
  }
}